import { useTranslation } from 'react-i18next';
import { Button } from '../UI/base/Button';
import { Tooltip } from '../UI';
import SaveIcon from '../../assets/icons/save.svg?react';
import SparklesIcon from '../../assets/icons/sparkles.svg?react';
import type { SimulationUnavailableReason } from '../../services/simulationAvailabilityService';
import { selectHasPendingChanges } from '../../store/application/applicationSlice';
import { useAppSelector } from '../../store/hooks';
import styles from './GanttEditToolbar.module.css';

interface EditActionsProps {
  visible: boolean;
  onDiscard: () => void | Promise<void>;
  onAccept: () => void | Promise<void>;
  // Absent where the page offers no simulation; the button is then left out.
  onSimulate?: () => void;
  // Set while the simulation cannot be used: the button is disabled and its
  // tooltip says why.
  simulationUnavailableReason?: SimulationUnavailableReason | null;
}

export const EditActions = ({
  visible,
  onDiscard,
  onAccept,
  onSimulate,
  simulationUnavailableReason
}: EditActionsProps) => {
  const { t } = useTranslation();
  // The plan's edits and the year target both count: a target typed in on its own
  // used to leave this greyed out, with Discard as the only way out of edit mode.
  const hasPendingChanges = useAppSelector(selectHasPendingChanges);

  return (
    <div className={`${styles.editActions} ${visible ? styles.editActionsVisible : ''}`}>
      <Button type="tertiary" onClick={onDiscard}>
        {t('edit-plan-discard')}
      </Button>
      {onSimulate && (
        <Tooltip
          disabled={!simulationUnavailableReason}
          trigger={
            <Button
              type="secondary"
              icon={<SparklesIcon className={styles.simulateIcon} />}
              onClick={onSimulate}
              disabled={!!simulationUnavailableReason}
              styles={{ root: { gap: 4, padding: 8 } }}
            >
              {t('edit-plan-simulate')}
            </Button>
          }
          content={
            simulationUnavailableReason && (
              <span className={styles.simulateTooltip}>{t(simulationUnavailableReason)}</span>
            )
          }
        />
      )}
      <Button
        type="primary"
        icon={<SaveIcon />}
        onClick={onAccept}
        disabled={!hasPendingChanges}
        styles={{ root: { gap: 4, padding: 12 } }}
      >
        {t('edit-plan-accept')}
      </Button>
    </div>
  );
};
